import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, slugify } from "@vinoga/db";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Power,
  Save,
  Sparkles,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { SlugInput } from "@/components/admin/SlugInput";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground transition-colors";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1.5 block";
const sectionClass = "bg-card border border-border rounded-xl p-6 shadow-sm space-y-5";

type FeaturedTile = {
  eyebrow?: string;
  head?: string;
  cta?: string;
  ctaHref?: string;
  image?: string;
  bg?: string;
};

type ParentOption = { id: string; name: string };

export function CategoryEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tr = useT();
  const { toast } = useToast();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [featuredOpen, setFeaturedOpen] = useState(false);

  const [form, setForm] = useState({
    slug: "",
    nameLv: "",
    nameEn: "",
    nameRu: "",
    parentId: "" as string,
    position: 0,
    color: "" as string,
    imageUrl: "" as string,
    active: true,
  });
  const [featured, setFeaturedState] = useState<FeaturedTile>({});
  // Full per-locale translations as loaded, so a name edit here doesn't wipe
  // description / meta / slug set from the Valodas translation hub.
  const [loadedTr, setLoadedTr] = useState<Record<string, Record<string, string>>>({});

  const [parents, setParents] = useState<ParentOption[]>([]);

  // Load top-level categories for the parent picker + (when editing) the row.
  useEffect(() => {
    (async () => {
      const { data: cats } = await supabase
        .from("categories")
        .select("id, parent_id, slug, translations, active")
        .is("parent_id", null)
        .eq("active", true);
      setParents(
        ((cats as any[]) ?? [])
          .filter((c) => c.id !== id)
          .map((c) => ({ id: c.id, name: c.translations?.lv?.name ?? c.slug }))
      );

      if (isNew) {
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("id", id!)
        .single();
      if (error || !data) {
        toast({ title: tr("common.notFound", "Not found"), variant: "destructive" });
        navigate("/categories");
        return;
      }
      const d = data as any;
      setLoadedTr((d.translations ?? {}) as Record<string, Record<string, string>>);
      setForm({
        slug: d.slug || "",
        nameLv: d.translations?.lv?.name ?? "",
        nameEn: d.translations?.en?.name ?? "",
        nameRu: d.translations?.ru?.name ?? "",
        parentId: d.parent_id ?? "",
        position: d.position ?? 0,
        color: d.color ?? "",
        imageUrl: d.image_url ?? "",
        active: d.active ?? true,
      });
      const feat = (d.meta?.featured ?? {}) as FeaturedTile;
      setFeaturedState(feat);
      setFeaturedOpen(Object.keys(feat).length > 0);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setFeat = (patch: Partial<FeaturedTile>) => {
    setFeaturedState((prev) => {
      const next = { ...prev, ...patch };
      for (const k of Object.keys(next) as Array<keyof FeaturedTile>) {
        if (next[k] === "") delete next[k];
      }
      return next;
    });
  };

  const clearFeatured = () => {
    setFeaturedState({});
    setFeaturedOpen(false);
  };

  const slugPreview = useMemo(
    () => form.slug.trim() || slugify(form.nameLv),
    [form.slug, form.nameLv]
  );

  const save = async () => {
    if (!form.nameLv.trim()) {
      toast({ title: tr("categories.nameRequired", "Name (LV) is required"), variant: "destructive" });
      return;
    }
    const slug = form.slug.trim() || slugify(form.nameLv);
    if (!slug) {
      toast({ title: tr("categories.slugRequired", "Slug is required"), variant: "destructive" });
      return;
    }
    setSaving(true);

    // Merge the edited name into whatever the row already has for each locale,
    // so translation-hub fields (description / meta / slug) survive a name edit.
    const mergeLoc = (loc: string, name: string) => {
      const merged: Record<string, string> = { ...(loadedTr[loc] ?? {}) };
      if (name.trim()) merged.name = name.trim();
      else delete merged.name;
      return Object.keys(merged).length ? merged : undefined;
    };
    const translations = {
      lv: mergeLoc("lv", form.nameLv)!,
      ...(mergeLoc("en", form.nameEn) ? { en: mergeLoc("en", form.nameEn) } : {}),
      ...(mergeLoc("ru", form.nameRu) ? { ru: mergeLoc("ru", form.nameRu) } : {}),
    };
    const meta = Object.keys(featured).length > 0 ? { featured } : {};

    const payload = {
      slug,
      translations,
      parent_id: form.parentId || null,
      position: form.position,
      color: form.color.trim() || null,
      image_url: form.imageUrl.trim() || null,
      active: form.active,
      meta,
    };

    try {
      if (isNew) {
        const { error } = await supabase.from("categories").insert(payload);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("categories").update(payload).eq("id", id!);
        if (error) throw error;
      }
      toast({
        title: isNew ? tr("common.created", "Created") : tr("common.saved", "Saved"),
      });
      navigate("/categories");
    } catch (err: any) {
      toast({
        title: tr("common.saveFailed", "Save failed"),
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {tr("common.loading", "Loading…")}
      </div>
    );
  }

  const isTopLevel = !form.parentId;

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-4 border-b border-border bg-background/85 px-4 py-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:px-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/categories")}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              {isNew
                ? tr("categories.newTitle", "New category")
                : `${tr("categories.editTitle", "Edit category")}: ${form.nameLv}`}
            </h1>
            <p className="font-body text-sm text-muted-foreground">
              {tr(
                "categories.editorSubtitle",
                "Top-level categories become storefront nav items; children populate the mega menu."
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/categories")} disabled={saving}>
            {tr("common.cancel", "Cancel")}
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            <Save size={18} />
            {saving
              ? tr("common.saving", "Saving…")
              : isNew
              ? tr("categories.create", "Create category")
              : tr("common.save", "Save changes")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <Sparkles className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("categories.sectionDetails", "Details")}
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>{tr("categories.fieldNameLv", "Name (LV)")} *</label>
                <input
                  className={inputClass}
                  value={form.nameLv}
                  onChange={(e) => setForm({ ...form, nameLv: e.target.value })}
                  placeholder="Sarkanvīni"
                />
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldNameEn", "Name (EN)")}</label>
                <input
                  className={inputClass}
                  value={form.nameEn}
                  onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldNameRu", "Name (RU)")}</label>
                <input
                  className={inputClass}
                  value={form.nameRu}
                  onChange={(e) => setForm({ ...form, nameRu: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldSlug", "Slug")}</label>
                <SlugInput
                  value={form.slug}
                  onChange={(next) => setForm({ ...form, slug: next })}
                  table="categories"
                  excludeId={isNew ? undefined : id}
                  sourceText={form.nameLv}
                  className={inputClass + " font-mono text-xs"}
                  placeholder={slugify(form.nameLv || "sarkanvini")}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {tr("categories.slugHint", "Storefront URL:")}{" "}
                  <span className="font-mono">/category/{slugPreview || "…"}</span>
                </p>
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldParent", "Parent category")}</label>
                <select
                  value={form.parentId}
                  onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                  className="h-[38px] w-full rounded-lg border border-border bg-card px-3 text-sm"
                >
                  <option value="">{tr("categories.parentNone", "— (top level)")}</option>
                  {parents.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldPosition", "Position")}</label>
                <input
                  type="number"
                  className={inputClass}
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: Number(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className={labelClass}>{tr("categories.fieldColor", "Accent color")}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.color || "#888888"}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-[38px] w-12 rounded-lg border border-border p-1"
                  />
                  <input
                    className={inputClass + " font-mono text-xs"}
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="#888888"
                  />
                </div>
              </div>
            </div>

            <Toggle
              icon={<Power size={18} />}
              label={tr("categories.fieldActive", "Active")}
              hint={tr("categories.activeHint", "Visible on the storefront")}
              checked={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
            />
          </section>

          {/* Featured tile (only meaningful for top-level / mega-menu categories) */}
          {isTopLevel && (
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => setFeaturedOpen((v) => !v)}
                className="flex w-full items-center gap-2 border-b border-border pb-2 text-left"
              >
                {featuredOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <Tag className="text-primary" size={18} />
                <h2 className="font-display text-base font-semibold">
                  {tr("categories.featuredTitle", "Mega-menu featured tile (optional)")}
                </h2>
              </button>
              {featuredOpen && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>{tr("categories.featuredEyebrow", "Eyebrow")}</label>
                    <input
                      className={inputClass}
                      value={featured.eyebrow ?? ""}
                      onChange={(e) => setFeat({ eyebrow: e.target.value })}
                      placeholder="Sezonas izlase"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{tr("categories.featuredCta", "CTA label")}</label>
                    <input
                      className={inputClass}
                      value={featured.cta ?? ""}
                      onChange={(e) => setFeat({ cta: e.target.value })}
                      placeholder="Iepirkties"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>{tr("categories.featuredHead", "Headline")}</label>
                    <textarea
                      className={inputClass + " resize-y"}
                      rows={2}
                      value={featured.head ?? ""}
                      onChange={(e) => setFeat({ head: e.target.value })}
                      placeholder={"Burgundijas\nvakari."}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{tr("categories.featuredCtaHref", "CTA link")}</label>
                    <input
                      className={inputClass}
                      value={featured.ctaHref ?? ""}
                      onChange={(e) => setFeat({ ctaHref: e.target.value })}
                      placeholder="/products?category=..."
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{tr("categories.featuredImage", "Background image URL")}</label>
                    <input
                      className={inputClass}
                      value={featured.image ?? ""}
                      onChange={(e) => setFeat({ image: e.target.value })}
                      placeholder="/img/plantation.webp"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{tr("categories.featuredBg", "Background color")}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={featured.bg ?? "#3F1F3D"}
                        onChange={(e) => setFeat({ bg: e.target.value })}
                        className="h-[38px] w-12 rounded-lg border border-border p-1"
                      />
                      <input
                        className={inputClass + " font-mono text-xs"}
                        value={featured.bg ?? ""}
                        onChange={(e) => setFeat({ bg: e.target.value })}
                        placeholder="#3F1F3D"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Button size="sm" variant="ghost" onClick={clearFeatured}>
                      {tr("categories.featuredClear", "Clear featured tile")}
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="space-y-6">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <ImageIcon className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("categories.sectionImage", "Category image")}
              </h2>
            </div>
            <div>
              <ImageUpload
                value={form.imageUrl}
                onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                bucket="categories"
                // Category art is a full-bleed tile background (the storefront
                // object-fit:covers it), so keep the natural aspect. Without
                // this the product-mode pass pads the photo onto a white square
                // and those bars show as lines around the image on the homepage.
                preserveAspectRatio
                placeholder={tr("categories.uploadHint", "Drop a category image here (or click to browse)")}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                {tr(
                  "categories.imageHint",
                  "Shown on category cards and the mega-menu tile. Falls back to the featured-tile image when empty."
                )}
              </p>
              {form.imageUrl && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-[11px] text-muted-foreground">
                    {tr("categories.showUrl", "Show / paste raw URL")}
                  </summary>
                  <input
                    className={inputClass + " mt-2 font-mono text-xs"}
                    value={form.imageUrl}
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  />
                </details>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  icon,
  label,
  hint,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`rounded-full p-2 ${
            checked ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          }`}
        >
          {icon}
        </div>
        <div>
          <p className="font-body text-sm font-medium text-foreground">{label}</p>
          <p className="font-body text-xs text-muted-foreground">{hint}</p>
        </div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 cursor-pointer rounded-sm"
      />
    </div>
  );
}
